import React, { useCallback, useEffect, useState } from 'react';
import { useCourseContext } from '../../CourseContext';
import { courseRunsService, LessonRun } from '../../../../services/courseRunsService';
import { useCourseBuild, LessonBuildState } from '../../shared/useCourseBuild';

interface RowState {
  label: string;
  badge: string;
  row: string;
  action: 'build' | 'retry' | null;
  tooltip: string;
}

function rowState(lr: LessonRun, bj: LessonBuildState | undefined): RowState {
  const compiled = lr.stages?.some(
    (s) => s.stage_id === 'lesson_compile' && s.status === 'succeeded',
  );
  if (bj?.status === 'queued' || bj?.status === 'processing') {
    return { label: 'Building', badge: 'bg-blue-100 text-blue-700', row: '', action: null, tooltip: '' };
  }
  if (bj?.status === 'failed') {
    return { label: 'Failed', badge: 'bg-red-100 text-red-700', row: 'bg-red-50', action: 'retry', tooltip: bj.error || 'Build failed' };
  }
  if (bj?.status === 'succeeded' && bj.warnings.length > 0) {
    const skipped = bj.warnings
      .map((w) => w.stage + (w.extraction_id ? `:${w.extraction_id}` : ''))
      .join(', ');
    const n = bj.warnings.length;
    return {
      label: `Built, ${n} warning${n > 1 ? 's' : ''}`,
      badge: 'bg-amber-100 text-amber-700',
      row: 'bg-amber-50',
      action: 'retry',
      tooltip: `Skipped: ${skipped}`,
    };
  }
  if (compiled) {
    return { label: 'Done', badge: 'bg-green-100 text-green-700', row: '', action: null, tooltip: '' };
  }
  if (lr.status === 'in_progress') {
    return { label: 'In Progress', badge: 'bg-blue-100 text-blue-700', row: '', action: null, tooltip: '' };
  }
  if (lr.status === 'published') {
    return { label: 'Published', badge: 'bg-purple-100 text-purple-700', row: '', action: null, tooltip: '' };
  }
  return { label: 'Not built', badge: 'bg-gray-100 text-gray-600', row: '', action: 'build', tooltip: '' };
}

const EST_LESSON_USD = 0.4; // rough per-lesson build cost (mirrors the backend estimate)

function lessonCost(lr: LessonRun): number {
  return (lr.stages || []).reduce((sum, s) => sum + (s.total_cost_usd || 0), 0);
}

function fmtUsd(n: number): string {
  return `$${n.toFixed(2)}`;
}

export function CourseLessonsDashboard() {
  const { run, setActiveLessonRunId } = useCourseContext();
  const isLocked = !!(run as any)?.syllabus_locked;

  const [lessonRuns, setLessonRuns] = useState<LessonRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runId = run?.run_id;

  const refreshLessons = useCallback(async () => {
    if (!runId) return;
    try {
      const result = await courseRunsService.listLessonRuns(runId);
      setLessonRuns(result.lesson_runs);
    } catch {
      // transient refresh failure: keep the current list; the next poll retries
    }
  }, [runId]);

  const { jobs: buildJobs, building, error: buildError, buildAll, buildLesson } =
    useCourseBuild(runId, refreshLessons);

  useEffect(() => {
    if (!runId || !isLocked) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await courseRunsService.listLessonRuns(runId);
        if (!cancelled) setLessonRuns(result.lesson_runs);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load lessons');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [runId, isLocked]);

  if (!isLocked) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        Lock the syllabus in the Review stage to create lesson runs.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-talaera-blue"></div>
        <p className="mt-3 text-sm text-gray-500">Loading lessons...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-700">{error}</div>
    );
  }

  const compiledCount = lessonRuns.filter((lr) =>
    lr.stages?.some((s) => s.stage_id === 'lesson_compile' && s.status === 'succeeded')
  ).length;
  const inProgressCount = lessonRuns.filter((lr) => lr.status === 'in_progress').length;
  const courseCost = lessonRuns.reduce((sum, lr) => sum + lessonCost(lr), 0);
  // "Build all" rebuilds every lesson, so estimate against the full count, not just the unbuilt.
  const buildAllEstimate = lessonRuns.length * EST_LESSON_USD;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Lessons Dashboard</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {compiledCount} of {lessonRuns.length} lessons completed
            {inProgressCount > 0 && `, ${inProgressCount} in progress`}
            {courseCost > 0 && ` · ${fmtUsd(courseCost)} generated`}
          </p>
        </div>
        {lessonRuns.length > 0 && compiledCount < lessonRuns.length && (
          <button
            onClick={() => void buildAll()}
            disabled={building}
            className="inline-flex items-center gap-2 rounded bg-talaera-blue px-3 py-1.5 text-sm font-medium text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {building ? (
              <>
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                Building {compiledCount}/{lessonRuns.length}...
              </>
            ) : (
              `Build all lessons (~${fmtUsd(buildAllEstimate)})`
            )}
          </button>
        )}
      </div>
      {buildError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {buildError}
        </div>
      )}

      {/* Progress bar */}
      {lessonRuns.length > 0 && (
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-green-500 rounded-full transition-all duration-300"
            style={{ width: `${(compiledCount / lessonRuns.length) * 100}%` }}
          />
        </div>
      )}

      {/* Lesson table */}
      <div className="bg-white border rounded overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead>
            <tr style={{ background: 'linear-gradient(135deg, #1a2332 0%, #2d3748 100%)' }}>
              <th className="px-3 py-2.5 text-left font-medium text-white/90 text-sm w-12">#</th>
              <th className="px-3 py-2.5 text-left font-medium text-white/90 text-sm">Lesson</th>
              <th className="px-3 py-2.5 text-left font-medium text-white/90 text-sm w-24">Type</th>
              <th className="px-3 py-2.5 text-left font-medium text-white/90 text-sm w-40">Status</th>
              <th className="px-3 py-2.5 text-right font-medium text-white/90 text-sm w-20">Cost</th>
              <th className="px-3 py-2.5 text-left font-medium text-white/90 text-sm w-24">Updated</th>
              <th className="px-3 py-2.5 text-right font-medium text-white/90 text-sm w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {lessonRuns.map((lr) => {
              const st = rowState(lr, buildJobs[lr.run_id]);
              return (
                <tr
                  key={lr.run_id}
                  className={`cursor-pointer transition-all ${st.row || 'hover:bg-gray-50'}`}
                  style={{ boxShadow: 'inset 0 0 0 0 #01bcd4' }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'inset 4px 0 0 0 #01bcd4'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'inset 0 0 0 0 #01bcd4'; }}
                  onClick={() => setActiveLessonRunId(lr.run_id)}
                >
                  <td className="px-3 py-3 text-gray-400 font-mono text-sm">{lr.lesson_number}</td>
                  <td className="px-3 py-3">
                    <div className="font-medium text-gray-900 text-[14px]">{lr.title}</div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      lr.lesson_type === 'test' ? 'bg-amber-100 text-amber-700' : 'text-gray-500'
                    }`}>
                      {lr.lesson_type}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span
                      title={st.tooltip || undefined}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${st.badge}`}
                    >
                      {st.label === 'Building' && (
                        <span className="h-2.5 w-2.5 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600" />
                      )}
                      {st.label}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right text-gray-500 text-sm tabular-nums">
                    {lessonCost(lr) > 0 ? fmtUsd(lessonCost(lr)) : ''}
                  </td>
                  <td className="px-3 py-3 text-gray-500 text-sm">
                    {new Date(lr.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {st.action && (
                      <button
                        onClick={(e) => { e.stopPropagation(); void buildLesson(lr.run_id); }}
                        className={`text-xs px-2 py-1 rounded font-medium border transition-colors ${
                          st.action === 'retry'
                            ? 'border-amber-300 text-amber-800 hover:bg-amber-100'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {st.action === 'retry' ? 'Retry' : 'Build'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {lessonRuns.length === 0 && (
              <tr>
                <td className="px-3 py-4 text-gray-500" colSpan={7}>
                  No lesson runs found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
